import React, { useState, useRef, useEffect } from 'react';
import { Download, Share2, Facebook, Twitter, Linkedin, Instagram, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { normalizeUserData, getUserFields } from '../services/userService';
import { ORGANS } from '../mockData';
import SEOHead from '../components/SEOHead';
import GenderAvatar from '../components/ui/gender-avatar';

const DigitalBadge = () => {
  const { user: authUser, isAuthenticated, loading: authLoading } = useAuth();
  const [showBloodType, setShowBloodType] = useState(true);
  const [showPicture, setShowPicture] = useState(false);
  const [userData, setUserData] = useState(null);
  const badgeRef = useRef(null);

  // Sync user data from localStorage on mount and when authUser changes
  useEffect(() => {
    if (isAuthenticated) {
      // Try to get latest data from localStorage first (most up-to-date)
      const storedUser = localStorage.getItem('auth_user');
      let userToUse = authUser;
      
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          // Use stored user if available, otherwise fall back to authUser
          userToUse = parsedUser || authUser;
        } catch (e) {
          // If parsing fails, use authUser
          userToUse = authUser;
        }
      }
      
      // Normalize user data to handle both snake_case and camelCase
      if (userToUse) {
        const normalized = normalizeUserData(userToUse);
        const userFields = getUserFields(normalized);
        setUserData(userFields);
      }
    } else {
      setUserData(null);
    }
  }, [authUser, isAuthenticated]);

  // Generic data for public users
  const genericUser = {
    name: 'Generic Donor',
    bloodType: 'O+',
    isBloodDonor: true,
    isOrganDonor: false,
    bodyDonation: false,
    organsPledged: [],
    profilePicture: null,
    inviteCode: 'GENERIC2025'
  };

  // Use normalized user data or generic data
  const user = isAuthenticated && userData ? {
    name: userData.name || 'Donor',
    bloodType: userData.bloodType || 'Unknown',
    isBloodDonor: userData.isBloodDonor || false,
    isOrganDonor: userData.isOrganDonor || false,
    bodyDonation: userData.bodyDonation || false,
    organsPledged: userData.organsPledged || [],
    profilePicture: userData.profilePicture || null,
    inviteCode: (userData.inviteCode || (authUser && (authUser.invite_code || authUser.inviteCode))) || 'UNKNOWN'
  } : genericUser;

  const handleDownload = async () => {
    try {
      if (badgeRef.current) {
        const canvas = await html2canvas(badgeRef.current, {
          backgroundColor: '#ffffff',
          scale: 2
        });
        const dataUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = 'donorshouse-badge.png';
        link.href = dataUrl;
        link.click();
        toast({ title: 'Badge Downloaded', description: 'Your badge has been downloaded successfully.' });
      }
    } catch (error) {
      toast({ title: 'Download Failed', description: 'Failed to download badge. Please try again.' });
    }
  };

  const handleShare = (platform) => {
    const text = encodeURIComponent(`I'm a proud donor on DonorsHouse! Join me to save lives. Use code ${user.inviteCode || 'INVITE123'}`);
    const url = encodeURIComponent(window.location.origin + '/badge');
    
    const shareUrls = {
      Facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      Twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      LinkedIn: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      Instagram: '#' // Instagram doesn't support direct web sharing
    };
    
    if (platform === 'Instagram') {
      toast({ 
        title: 'Download & Share', 
        description: 'Download the badge and share it manually on Instagram!'
      });
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  // Generate dynamic description for social sharing
  const shareDescription = isAuthenticated && userData
    ? `I'm ${user.name}, a proud donor on DonorsHouse! Join me to save lives. Use code ${user.inviteCode || 'INVITE123'}`
    : "Join DonorsHouse to save lives through blood and organ donation. Connect with donors worldwide.";

  return (
    <>
      <SEOHead
        title="DonorsHouse Digital Badge"
        description={shareDescription}
        image="/favicon.png"
        type="website"
      />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Digital Badge</h1>
          <p className="text-gray-700 text-lg">Generate and share your donor badge to inspire others</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Badge Preview */}
          <div>
            <Card className="shadow-lg overflow-hidden" ref={badgeRef}>
              {authLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading badge...</p>
                </div>
              ) : (
                <>
                  <div className="bg-gradient-to-br from-red-700 to-red-900 p-8 text-center">
                <div className="inline-block bg-white rounded-full p-1 mb-4">
                  <GenderAvatar
                    profilePicture={showPicture ? user.profilePicture : null}
                    gender={user.gender}
                    size={96}
                    className="w-24 h-24"
                  />
                </div>
                
                <h2 className="text-2xl font-bold text-white mb-2">{user.name}</h2>
                
                {showBloodType && (
                  <div className="mb-4">
                    <Badge className="bg-white text-red-950 text-2xl px-6 py-2">
                      {user.bloodType}
                    </Badge>
                  </div>
                )}

                <div className="space-y-2 mb-6">
                  {user.isBloodDonor && (
                    <div className="flex items-center justify-center">
                      <div className="bg-white bg-opacity-20 px-4 py-2 rounded-full">
                        <span className="text-white font-semibold">♥ Blood Donor</span>
                      </div>
                    </div>
                  )}
                  {user.isOrganDonor && (
                    <div className="flex items-center justify-center">
                      <div className="bg-white bg-opacity-20 px-4 py-2 rounded-full">
                        <span className="text-white font-semibold">♥ Organ Donor</span>
                      </div>
                    </div>
                  )}
                  {user.bodyDonation && (
                    <div className="flex items-center justify-center">
                      <div className="bg-white bg-opacity-20 px-4 py-2 rounded-full">
                        <span className="text-white font-semibold">♥ Body Donation</span>
                      </div>
                    </div>
                  )}
                </div>

                {user.organsPledged && user.organsPledged.length > 0 && (
                  <div className="mb-6">
                    <p className="text-white text-sm font-semibold mb-2">Organs Pledged:</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {user.organsPledged.map((organId, index) => {
                        // Map organ ID to display name (matching MyProfile.jsx logic)
                        const organ = ORGANS.find(o => o.id === organId);
                        const displayName = organ ? organ.name : organId;
                        return (
                          <span key={index} className="bg-white bg-opacity-30 px-3 py-1 rounded-full text-white text-xs">
                            {displayName}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="border-t border-white border-opacity-30 pt-4 mt-4">
                  <p className="text-white text-sm italic">"Supporting humanity right through"</p>
                </div>
              </div>
              
              <div className="bg-white p-4 text-center border-t-4 border-red-700">
                <div className="flex items-center justify-center space-x-2">
                  <img src="/favicon.png" alt="DonorsHouse" className="w-6 h-6" />
                  <span className="font-bold text-gray-900 text-lg">DonorsHouse</span>
                </div>
                  <p className="text-xs text-gray-600 mt-1">Join the movement at donorshouse.com</p>
                </div>
                </>
              )}
            </Card>
          </div>

          {/* Customization Options */}
          <div className="space-y-6">
            {isAuthenticated ? (
              <>
                <Card className="shadow-md">
                  <CardHeader>
                    <CardTitle className="text-gray-900">Customize Badge</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="show-blood-type"
                        checked={showBloodType}
                        onCheckedChange={setShowBloodType}
                      />
                      <Label htmlFor="show-blood-type" className="text-gray-900 cursor-pointer">
                        Show Blood Type
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="show-picture"
                        checked={showPicture}
                        onCheckedChange={setShowPicture}
                      />
                      <Label htmlFor="show-picture" className="text-gray-900 cursor-pointer">
                        Show Profile Picture
                      </Label>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-md">
                  <CardHeader>
                    <CardTitle className="text-gray-900">Download & Share</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button
                      onClick={handleDownload}
                      className="w-full bg-red-700 hover:bg-red-900 text-white btn-transition"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Badge
                    </Button>

                    <div className="border-t border-gray-200 pt-3">
                      <p className="text-sm text-gray-700 font-semibold mb-3">Share on Social Media</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          onClick={() => handleShare('Facebook')}
                          className="text-gray-900 border-gray-900 hover:bg-gray-100"
                        >
                          <Facebook className="h-4 w-4 mr-2" />
                          Facebook
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleShare('Twitter')}
                          className="text-gray-900 border-gray-900 hover:bg-gray-100"
                        >
                          <Twitter className="h-4 w-4 mr-2" />
                          Twitter
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleShare('LinkedIn')}
                          className="text-gray-900 border-gray-900 hover:bg-gray-100"
                        >
                          <Linkedin className="h-4 w-4 mr-2" />
                          LinkedIn
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleShare('Instagram')}
                          className="text-gray-900 border-gray-900 hover:bg-gray-100"
                        >
                          <Instagram className="h-4 w-4 mr-2" />
                          Instagram
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-md border-green-200">
                  <CardContent className="pt-6">
                    <h3 className="font-bold text-gray-900 mb-2">Inspire Others</h3>
                    <p className="text-gray-700 text-sm">
                      Share your badge on social media to encourage your friends and family to join the cause.
                      Every person you inspire can potentially save lives!
                    </p>
                    <p className="text-sm text-gray-600 mt-3">
                      Use invite code <span className="font-mono font-bold text-red-700">{user.inviteCode}</span> to track your network growth.
                    </p>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="shadow-md border-blue-200">
                <CardContent className="pt-6 text-center">
                  <LogIn className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="font-bold text-gray-900 mb-2">Sign in to Customize Your Badge</h3>
                  <p className="text-gray-700 text-sm mb-4">
                    Sign in to view your personal badge, customize its appearance, and download or share it with others.
                  </p>
                  <Link to="/signin">
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                      <LogIn className="h-4 w-4 mr-2" />
                      Sign In to Access Your Badge
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default DigitalBadge;